import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Select, type SelectOption } from '../../atoms'

interface PaginationProps {
  total: number
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
}

export const Pagination = memo(
  ({ total, page, pageSize, onPageChange, onPageSizeChange }: PaginationProps) => {
    const { t } = useTranslation('admin-mobifull')
    const totalPages = Math.max(1, Math.ceil(total / pageSize))
    const pages = Array.from({ length: totalPages }, (_, i) => i + 1)

    const pageSizeOptions: SelectOption[] = useMemo(
      () =>
        [10, 20, 50].map(s => ({
          value: s,
          label: t('pagination.perPage', { count: s }),
        })),
      [t],
    )

    return (
      <div className="pagination">
        <div className="pagination__size-selector">
          <Select
            value={pageSize}
            onChange={val => onPageSizeChange(Number(val))}
            options={pageSizeOptions}
            aria-label={t('pagination.pageSizeSelector')}
          />
        </div>
        <div className="pagination__controls">
          <Button
            variant="ghost"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            aria-label="Previous page">
            « {t('pagination.prev')}
          </Button>
          <div className="pagination__pages">
            {pages.map(p => (
              <Button
                key={p}
                variant={p === page ? 'primary' : 'ghost'}
                onClick={() => onPageChange(p)}
                disabled={p === page}
                className={p === page ? 'pagination__page--active' : 'pagination__page'}>
                {p}
              </Button>
            ))}
          </div>
          <Button
            variant="ghost"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            aria-label="Next page">
            {t('pagination.next')} »
          </Button>
        </div>
      </div>
    )
  },
)

Pagination.displayName = 'Pagination'
